// Service-user assessment form-filling milestones (the first ring-fenced
// slice of the BigQuery event stream — analytics/ANALYTICS_BQ_OVERHAUL_PHASE0.md).
//
// One thin helper per milestone so call sites stay one-liners and every
// payload is assembled in a single reviewed place:
//   form started    -> assessment_form_started   (+ PII-free device fields)
//   form saved      -> assessment_form_saved     (throttled per form)
//   form completed  -> assessment_form_completed
//   all forms done  -> assessment_forms_all_completed
//   feedback given  -> assessment_feedback_submitted
//
// Saves fire on every answer for engaged users; a per-form throttle keeps
// the stream at "journey milestone" granularity (first save immediately,
// then at most one per interval). Started/completed events are not
// throttled — they are write-once by construction at the call sites.
//
// All helpers are fire-and-forget and never throw: analytics must not
// break a service user's questionnaire flow.

import { emitAnalyticsEvent } from './emitEvent';
import { getAnalyticsDeviceFields } from './deviceFields';
import { MAX_STRING_LEN } from './eventRegistry';

const SAVE_THROTTLE_MS = 5 * 60 * 1000;
const lastSaveEmit = new Map();

// Once-per-session guard for form_started: the Firestore firstStartedAt
// write-once field suppresses repeats across sessions, but within a session
// a component can re-mount before that write round-trips (React StrictMode
// double-effects in dev, quick back-and-forth navigation in prod) and would
// emit twice. First emit wins.
const startedEmitted = new Set();

const ROLES = ['patient', 'informant'];
const ASSESSMENT_TYPES = ['adhd', 'autism', 'cyp_adhd'];

// Version 2 of the service-user milestone events. Version 1 set patient_id to
// the acting account's own uid, which for an informant is the informant, not
// the person being assessed — so informant rows described the wrong subject.
// From version 2, patient_id is always the assessed patient on every surface,
// and the informant remains identifiable as the envelope's actor_id. The
// version is what lets analysis separate the two eras instead of guessing by
// date. The feedback prompt/dismissed events are new in this release and have
// no earlier era, so they stay at the default version 1.
const SU_FORM_EVENT_VERSION = 2;
const V2 = { eventVersion: SU_FORM_EVENT_VERSION };

// Claims carry userType 'Patient'/'Informant' and assessmentModule
// 'adultAdhd'/'adultAutism'/'cypAdhd'; events carry the canonical enums.
export function roleFromUserType(userType) {
  const role = String(userType || '').toLowerCase();
  return ROLES.includes(role) ? role : null;
}

export function assessmentTypeFromModule(assessmentModule) {
  if (assessmentModule === 'adultAdhd') return 'adhd';
  if (assessmentModule === 'adultAutism') return 'autism';
  if (assessmentModule === 'cypAdhd') return 'cyp_adhd';
  return null;
}

// ADHD progress percentages are fractional (e.g. 47.058); events carry whole
// clamped percentages.
function toProgressPct(value) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined;
  return Math.max(0, Math.min(100, Math.round(value)));
}

function baseFields({ patientId, formKey, role, assessmentType }) {
  if (!patientId || !ROLES.includes(role) || !ASSESSMENT_TYPES.includes(assessmentType)) {
    return null;
  }
  const fields = {
    patient_id: patientId,
    role,
    assessment_type: assessmentType,
  };
  if (formKey) fields.form_key = String(formKey);
  return fields;
}

export function emitFormStarted(context) {
  const fields = baseFields(context);
  if (!fields || !fields.form_key) return false;
  const onceKey = `${fields.patient_id}:${fields.form_key}`;
  if (startedEmitted.has(onceKey)) return false;
  startedEmitted.add(onceKey);
  return emitAnalyticsEvent('assessment_form_started', {
    ...fields,
    ...getAnalyticsDeviceFields(),
  }, V2);
}

export function emitFormSaved(context, { progressPct } = {}) {
  const fields = baseFields(context);
  if (!fields || !fields.form_key) return false;

  const throttleKey = `${fields.patient_id}:${fields.form_key}`;
  const now = Date.now();
  const last = lastSaveEmit.get(throttleKey);
  if (last !== undefined && now - last < SAVE_THROTTLE_MS) return false;
  lastSaveEmit.set(throttleKey, now);

  const payload = { ...fields };
  const pct = toProgressPct(progressPct);
  if (pct !== undefined) payload.progress_pct = pct;
  return emitAnalyticsEvent('assessment_form_saved', payload, V2);
}

// Failed saves are the "not client-side-only" corroboration signal the
// feedback triage work-start policy looks for. Error CODES only (never
// messages), sanitised to a short slug; throttled per form so client retry
// storms count once per window rather than flooding.
const FAILURE_THROTTLE_MS = 60 * 1000;
const lastFailureEmit = new Map();

function toErrorCode(value) {
  const slug = String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9/_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
  return slug || 'unknown';
}

export function emitFormSaveFailed(context, { errorCode } = {}) {
  const fields = baseFields(context);
  if (!fields || !fields.form_key) return false;

  const throttleKey = `${fields.patient_id}:${fields.form_key}`;
  const now = Date.now();
  const last = lastFailureEmit.get(throttleKey);
  if (last !== undefined && now - last < FAILURE_THROTTLE_MS) return false;
  lastFailureEmit.set(throttleKey, now);

  return emitAnalyticsEvent('assessment_form_save_failed', {
    ...fields,
    error_code: toErrorCode(errorCode),
  }, V2);
}

export function emitFormCompleted(context, { progressPct } = {}) {
  const fields = baseFields(context);
  if (!fields || !fields.form_key) return false;
  const payload = { ...fields };
  const pct = toProgressPct(progressPct);
  if (pct !== undefined) payload.progress_pct = pct;
  return emitAnalyticsEvent('assessment_form_completed', payload, V2);
}

export function emitAllFormsCompleted(context, { formCount } = {}) {
  const fields = baseFields({ ...context, formKey: undefined });
  if (!fields) return false;
  const payload = { ...fields };
  if (Number.isInteger(formCount)) payload.form_count = formCount;
  return emitAnalyticsEvent('assessment_forms_all_completed', payload, V2);
}

// Feedback surfaces that can raise the modal; mirrors the Firestore
// formFeedback.type values so BQ rows join cleanly against the doc data. The
// modal imports its own trigger from here: one string serves both the Firestore
// write and the event, so the two cannot drift and silently drop the field.
export const FEEDBACK_TRIGGER_ALL_FORMS_COMPLETED = 'allFormsCompleted';

const FEEDBACK_TRIGGERS = [FEEDBACK_TRIGGER_ALL_FORMS_COMPLETED];

function feedbackFields(context, trigger) {
  const fields = baseFields({ ...context, formKey: undefined });
  if (!fields) return null;
  if (FEEDBACK_TRIGGERS.includes(trigger)) fields.trigger = trigger;
  return fields;
}

// The feedback denominator. Submissions alone cannot answer "what is our
// response rate" or "where do people give up" — prompt_shown supplies the
// denominator and dismissed supplies the drop-off stage, so feedback becomes
// a funnel we can optimise rather than a count we can only watch.
export function emitFeedbackPromptShown(context, { trigger } = {}) {
  const fields = feedbackFields(context, trigger);
  if (!fields) return false;
  return emitAnalyticsEvent('assessment_feedback_prompt_shown', fields);
}

export function emitFeedbackDismissed(
  context,
  { trigger, taskRating, platformRating, hasText } = {},
) {
  const fields = feedbackFields(context, trigger);
  if (!fields) return false;
  const answered = [taskRating, platformRating].filter(
    (value) => Number.isInteger(value) && value > 0,
  ).length;
  const stage =
    answered === 0 ? 'no_ratings' : answered === 1 ? 'ratings_partial' : 'ratings_complete';
  return emitAnalyticsEvent('assessment_feedback_dismissed', {
    ...fields,
    stage,
    has_text: Boolean(hasText),
  });
}

export function emitFeedbackSubmitted(
  context,
  { taskRating, platformRating, hasText, question, trigger } = {},
) {
  const fields = feedbackFields(context, trigger);
  if (!fields) return false;
  const payload = { ...fields, has_text: Boolean(hasText) };
  if (Number.isInteger(taskRating)) payload.task_rating = taskRating;
  if (Number.isInteger(platformRating)) payload.platform_rating = platformRating;
  // The question is our own copy shown to the user (never their text), captured
  // verbatim so future wording changes don't orphan old rows. Truncated to the
  // registry string cap so a longer future prompt degrades the field, not the event.
  const questionText =
    typeof question === 'string' ? question.trim().slice(0, MAX_STRING_LEN) : '';
  if (questionText) payload.question = questionText;
  return emitAnalyticsEvent('assessment_feedback_submitted', payload, V2);
}

// Test-only: clear the per-form throttles and started guard between tests.
export function __resetFormEventThrottleForTests() {
  lastSaveEmit.clear();
  lastFailureEmit.clear();
  startedEmitted.clear();
}
